import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import DashboardLayout from "src/components/layout/dashboard/Layout";
import PageHeader from "src/components/common/PageHeader";
import Card from "src/components/common/card/Card";
import Input from "src/components/ui/Input";
import Textarea from "src/components/ui/Textarea";
import Dropdown from "src/components/ui/Dropdown";
import TableV2 from "src/components/ui/TableV2";
import Button from "src/components/ui/Button";
import SearchInput, { SearchInputOption } from "src/components/ui/SearchInput";

import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as adjustmentOrderActions from "src/redux/slices/adjustmentOrderSlice";
import { toast } from "react-toastify";
import productApis from "src/api/product";
import { AdjustmentOrderProduct } from "src/types/adjustmentOrder.types";
import { Product } from "src/types/product.types";
import { Trash } from "lucide-react";
import adjustmentOrderApis from "src/api/adjustmentOrder";

const AdjustmentForm: React.FC = () => {
  const { adjustmentId } = useParams();
  const navigate = useNavigate();

  const dispatch = useAppDispatch();
  const {
    user: { data: user },
  } = useAppSelector((state) => state.authTenant);
  const { create, edit } = useAppSelector((state) => state.adjustmentOrder);
  const isTenantAdmin =
    user?.role === "admin" ||
    user?.roleDetails?.name?.toLowerCase() === "admin";

  // ------------------ State ------------------
  const [adjustList, setAdjustList] = useState<AdjustmentOrderProduct[]>([]);
  const [remarks, setRemarks] = useState("");
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  useEffect(() => {
    const loadAdjustmentDetails = async () => {
      if (!adjustmentId) return;
      try {
        setIsLoadingDetails(true);
        const res = await adjustmentOrderApis.getDetails(adjustmentId);
        const order = res?.data;
        if (!order) {
          toast.error("Adjustment order not found");
          navigate("/app/adjustment/list");
          return;
        }
        const createdById =
          typeof order?.createdBy === "string"
            ? order.createdBy
            : order?.createdBy?._id;
        if (
          order?.orderStatus === "draft" &&
          !isTenantAdmin &&
          createdById &&
          user?._id &&
          String(createdById) !== String(user._id)
        ) {
          toast.error("Only the creator can edit this draft adjustment");
          navigate("/app/adjustment/list");
          return;
        }

        const mapped: AdjustmentOrderProduct[] =
          order.products?.map((item: any) => ({
            productId:
              typeof item.productId === "string"
                ? item.productId
                : item.productId?._id || "",
            name: item.productId?.name || item.name || "",
            sku: item.productId?.sku || item.sku,
            hsnCode: item.productId?.hsnCode || item.hsnCode,
            quantity: Number(item.quantity || 0),
            direction: item.direction || "in",
            reason: item.reason || "",
          })) || [];

        setAdjustList(mapped);
        setRemarks(order.remarks || "");

        if (order.orderStatus !== "draft") {
          toast.error("Only draft adjustments can be edited");
          navigate("/app/adjustment/list");
        }
      } catch (error) {
        console.error("Error fetching adjustment details:", error);
        toast.error("Failed to fetch adjustment details");
      } finally {
        setIsLoadingDetails(false);
      }
    };

    if (adjustmentId) {
      loadAdjustmentDetails();
    }
  }, [adjustmentId, navigate, isTenantAdmin, user?._id]);

  const handleClose = useCallback(() => {
    dispatch(adjustmentOrderActions.resetCreateAdjustmentOrder());
    dispatch(adjustmentOrderActions.resetEditAdjustmentOrder());
    navigate("/app/adjustment/list");
  }, [dispatch, navigate]);

  useEffect(() => {
    if (create.data) {
      toast.success("Stock adjustment created successfully!");
      handleClose();
    } else if (create.error) {
      toast.error(`Error: ${create.error}`);
    }
  }, [create, handleClose]);

  useEffect(() => {
    if (edit.data) {
      toast.success("Stock adjustment updated successfully!");
      handleClose();
    } else if (edit.error) {
      toast.error(`Error: ${edit.error}`);
    }
  }, [edit, handleClose]);

  // ------------------ Add / Update / Remove ------------------
  const handleAddProduct = (product: Product) => {
    if (adjustList.some((a) => a.productId === product._id)) {
      toast.warning("Product already added");
      return;
    }

    setAdjustList([
      ...adjustList,
      {
        productId: product._id || "",
        name: product.name || "",
        sku: product.sku,
        hsnCode: product.hsnCode,
        quantity: 1,
        direction: "in",
        reason: "",
      },
    ]);
  };

  const handleUpdateItem = (
    index: number,
    field: keyof AdjustmentOrderProduct,
    value: any
  ) => {
    const updated = [...adjustList];
    // @ts-ignore
    updated[index][field] = value;
    setAdjustList(updated);
  };

  const handleRemoveItem = (index: number) => {
    setAdjustList(adjustList.filter((_, i) => i !== index));
  };

  // ------------------ Submit ------------------
  const handleSubmit = (status: "draft" | "completed" = "completed") => {
    if (adjustList.length === 0) {
      toast.error("Please add at least one product adjustment");
      return;
    }

    const payload = {
      products: adjustList.map((a) => ({
        productId: a.productId,
        name: a.name,
        quantity: Number(a.quantity),
        direction: a.direction,
        reason: a.reason,
      })),
      remarks,
      createdBy: user?._id || "",
      orderStatus: status,
    };

    if (adjustmentId) {
      dispatch(
        adjustmentOrderActions.editAdjustmentOrderRequest({
          id: adjustmentId,
          data: payload,
        })
      );
      return;
    }

    dispatch(adjustmentOrderActions.createAdjustmentOrderRequest(payload));
  };

  const pageTitle = adjustmentId ? "Edit Adjustment" : "Create Adjustment";

  return (
    <DashboardLayout
      meta={{
        title: adjustmentId
          ? "Edit Adjustment | Invyntra"
          : "Create Adjustment | Invyntra",
        description: "Create stock adjustments.",
      }}
    >
      <div className="space-y-4">
        <PageHeader
          title=""
          showBack={!!adjustmentId}
          breadcrumbData={[
            { label: "Home", path: "/app/dashboard" },
            { label: "Adjustments", path: "/app/adjustment/list" },
            { label: pageTitle, path: "" },
          ]}
        />

        <Card>
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-800 text-center">
              {pageTitle}
            </h2>

            {/* ---------------- PRODUCTS ---------------- */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                Adjust Inventory
              </h3>

              <SearchInput<Product>
                label="Add Product"
                placeholder="Search by name, SKU, HSN, or product ID..."
                clearOnSelect
                onSearch={async (query) => {
                  const res = await productApis.getProductList({
                    page: 1,
                    limit: 10,
                    status: "active",
                    search: query,
                  });
                  const list: Product[] = res?.data?.data || res?.data || [];
                  return list.map((p) => ({
                    value: p._id || "",
                    label: `${p.name} (${p.sku || p.productId})`,
                    data: p,
                  }));
                }}
                onSelect={(option: SearchInputOption<Product>) =>
                  handleAddProduct(option.data)
                }
                renderOption={(option) => (
                  <div>
                    <div className="font-medium text-gray-800">
                      {option.data.name}
                    </div>
                    <div className="text-xs text-gray-500">
                      SKU: {option.data.sku || "-"} · HSN:{" "}
                      {option.data.hsnCode || "-"} · ID:{" "}
                      {option.data.productId}
                    </div>
                  </div>
                )}
              />

              {adjustList.length > 0 && (
                <div className="mt-4">
                  <TableV2
                    columns={[
                      {
                        key: "name",
                        header: "Product",
                        accessor: ({ row }) => (
                          <div>
                            <div className="font-medium text-gray-800">
                              {row.name}
                            </div>
                            <div className="text-xs text-gray-500">
                              SKU: {row.sku || "-"} · HSN: {row.hsnCode || "-"}
                            </div>
                          </div>
                        ),
                      },
                      {
                        key: "quantity",
                        header: "Qty",
                        width: "90px",
                        accessor: ({ row, rowIndex }) => (
                          <Input
                            type="number"
                            inputClassName="text-center"
                            value={row.quantity}
                            onChange={(e) =>
                              handleUpdateItem(
                                rowIndex,
                                "quantity",
                                e.target.value
                              )
                            }
                          />
                        ),
                      },
                      {
                        key: "direction",
                        header: "Direction",
                        width: "160px",
                        accessor: ({ row, rowIndex }) => (
                          <Dropdown
                            value={row.direction}
                            options={[
                              { label: "Add Stock", value: "in" },
                              { label: "Remove Stock", value: "out" },
                            ]}
                            onChange={(val) =>
                              handleUpdateItem(rowIndex, "direction", val)
                            }
                            fullWidth
                          />
                        ),
                      },
                      {
                        key: "reason",
                        header: "Reason",
                        accessor: ({ row, rowIndex }) => (
                          <Input
                            type="text"
                            placeholder="e.g. Damaged, Expired, Correction"
                            value={row.reason || ""}
                            onChange={(e) =>
                              handleUpdateItem(
                                rowIndex,
                                "reason",
                                e.target.value
                              )
                            }
                          />
                        ),
                      },
                      {
                        key: "action",
                        header: "",
                        width: "48px",
                        accessor: ({ rowIndex }) => (
                          <button
                            onClick={() => handleRemoveItem(rowIndex)}
                            className="hover:bg-red-100 text-red-600 px-2 py-2 rounded-md"
                          >
                            <Trash size={16} />
                          </button>
                        ),
                      },
                    ]}
                    data={adjustList}
                  />
                </div>
              )}
            </div>

            {/* ---------------- REMARKS ---------------- */}
            <div className="border rounded-lg p-4 bg-gray-50 space-y-3">
              <Textarea
                label="Remarks"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="Additional notes..."
              />
            </div>

            {/* ---------------- ACTION BUTTONS ---------------- */}
            <div className="flex justify-end space-x-3 pb-2">
              <Button onClick={handleClose} variant="outline">
                Cancel
              </Button>
              <Button
                onClick={() => handleSubmit("draft")}
                variant="outline"
                isLoading={create.loading || edit.loading || isLoadingDetails}
                disabled={!adjustList.length || isLoadingDetails}
              >
                Save as Draft
              </Button>
              <Button
                onClick={() => handleSubmit("completed")}
                isLoading={create.loading || edit.loading || isLoadingDetails}
                disabled={!adjustList.length || isLoadingDetails}
                className="bg-indigo-600 text-white"
              >
                Place Order
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default AdjustmentForm;
